import PropTypes from "prop-types";
import { useState, useContext, useEffect, useRef, Fragment } from "react";
import _ from "lodash";
import { JssProvider, createUseStyles } from "react-jss";
import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import { MSFNavButton } from "../common/Button";
import jssVariables from "../../jss_setup";
import { Placeholder } from "rsuite";
import { CheckPickerField } from "../forms/inputGroup";
import { InlineIcon } from "@iconify/react";
import { useSelector } from "react-redux";
import { usePropertyLangTag } from "../../hooks";
import { cStyler } from "../../helpers";
import { GlobalModalsContext } from "../../context";

export function inputGroupStyles() {
  return {
    display: "grid",
    textAlign: "left",
    "& input, button": {
      height: "3rem",
    },
  };
}

function getProductName(product_type: string) {
  switch (product_type) {
    case "MI":
      return "tours";
    case "PKG":
      return "packages";
    case "FL":
      return "flights";
    case "ACT":
      return "activities";
    case "REST":
      return "restaurants";
    case "TRF":
      return "transfers";
    case "CAR":
      return "carRentals";
    case "TRA":
      return "trains";
    case "FER":
      return "ferries";
    default:
      return "";
  }
}

const durStyles = createUseStyles({
  DurationsPicker: { ...inputGroupStyles() },
  label: (props) => ({ ...(props?.customStyles?.label ?? {}) }),
});
export function DurationsPicker({ ghost, durations, value, customStyles, onChange }) {
  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  if (ghost) return <Placeholder.Paragraph graph="square" active />;

  const classes = durStyles({ customStyles });

  return (
    <div className={classes.DurationsPicker}>
      <span className={classes.label}>
        <strong>{t("packageDuration")}</strong>
      </span>
      <CheckPickerField
        value={value}
        searchable={false}
        data={durations.map((dur: { nights: number; days: number }) => ({
          value: dur.nights,
          label: `${dur.nights} Nights, ${dur.days} Days`,
        }))}
        onChange={onChange}
      />
    </div>
  );
}
DurationsPicker.defaultProps = {
  customStyles: {},
};
DurationsPicker.propTypes = {
  customStyles: PropTypes.object.isRequired,
  ghost: PropTypes.bool.isRequired,
  durations: PropTypes.array.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

const srvStyles = createUseStyles({
  ServicesPicker: { ...inputGroupStyles() },
  label: (props) => ({ ..._.get(props, "customStyles.label", {}) }),
});
export function ServicesPicker(props) {
  const { customStyles, services, ghost, value, onChange } = props;
  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  if (ghost) {
    return <Placeholder.Paragraph graph="square" active />;
  }

  const classes = srvStyles({ customStyles });

  return (
    <div className={classes.ServicesPicker}>
      <span className={classes.label}>
        <strong>{t("includedServices")}</strong>
      </span>
      <CheckPickerField
        value={value}
        searchable={false}
        data={[
          { value: "FL", label: t("flights") },
          { value: "ACC", label: t("accommodation") },
          { value: "TRF", label: t("transfers") },
          { value: "MI", label: `${t("tours")}/${t("experiences")}` },
        ].filter((srv) => services.includes(srv.value))}
        onChange={onChange}
      />
    </div>
  );
}
ServicesPicker.defaultProps = {
  customStyles: {},
};
ServicesPicker.propTypes = {
  customStyles: PropTypes.object.isRequired,
  services: PropTypes.array.isRequired,
  ghost: PropTypes.bool.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

const paxPickerStyles = createUseStyles({
  PaxPicker: (props) => ({
    ...inputGroupStyles(),
    position: "relative",
    alignContent: "space-between",
    fontFamily: jssVariables.fontFamily,
    ..._.get(props, "customStyles.PaxPicker", {}),
  }),
  label: (props) => ({
    color: jssVariables.colors.text.dark,
    fontSize: "medium",
    ..._.get(props, "customStyles.label", {}),
  }),
  PaxBtn: (props) => ({
    borderRadius: "5px",
    height: "2.45rem",
    textAlign: "left",
    fontSize: "medium",
    background: "white",
    border: `1px solid ${jssVariables.colors.border}`,
    color: jssVariables.colors.text.dark,
    width: "100%",
    paddingLeft: jssVariables.normal_gap,
    ..._.get(props, "customStyles.paxBtn", {}),
  }),
});
export function PaxPicker({
  id,
  customStyles,
  adults,
  seniors,
  childs,
  youths,
  rooms,
  infantsOnLap,
  infantsInSeat,
  onClick,
  product_type,
  showLabel,
}) {
  const classes = paxPickerStyles({ customStyles, showLabel });
  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  const childPax = childs + infantsOnLap + infantsInSeat;
  return (
    <div className={classes.PaxPicker}>
      <span className={classes.label}>{showLabel && t("guests")}</span>
      <button
        id={id}
        className={classes.PaxBtn}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onClick();
        }}
      >
        {t("adults")}: {adults}
        {/* {product_type === "FER" && ` - ${t("seniors")}: ${seniors}`} */}
        {product_type === "TRA" && ` - ${t("seniors")}: ${seniors}`}
        {product_type !== "TRA" && ` - ${t("children")}: ${childPax}`}
        {product_type === "TRA" && ` - ${t("youths")}: ${youths}`}
        {product_type === "TRA" && ` - ${t("children")}: ${childs}`}
        {product_type === "ACC" && ` - ${t("rooms")}: ${rooms}`}
      </button>
    </div>
  );
}
PaxPicker.defaultProps = {
  customStyles: {},
  adults: 2,
  seniors: 0,
  childs: 0,
  youths: 0,
  children_ages: [],
  rooms: 1,
  infantsOnLap: 0,
  infantsInSeat: 0,
  showLabel: true,
  product_type: "ACC",
  id: "",
};
PaxPicker.propTypes = {
  customStyles: PropTypes.object.isRequired,
  seniors: PropTypes.number,
  adults: PropTypes.number.isRequired,
  childs: PropTypes.number.isRequired,
  youths: PropTypes.number.isRequired,
  children_ages: PropTypes.array.isRequired,
  infantsOnLap: PropTypes.number.isRequired,
  infantsInSeat: PropTypes.number.isRequired,
  rooms: PropTypes.number.isRequired,
  showLabel: PropTypes.bool,
  product_type: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  id: PropTypes.string,
};

const railPaxPickerStyles = createUseStyles({
  RailPaxPicker: (props) => ({
    ...inputGroupStyles(),
    position: "relative",
    height: "100%",
    alignContent: "space-between",
    ..._.get(props, "customStyles.RailPaxPicker", {}),
  }),
  label: (props) => ({
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.label", {}),
  }),
  paxBtn: (props) => ({
    borderRadius: "5px",
    height: "2.45rem",
    textAlign: "left",
    fontSize: "medium",
    background: "white",
    border: `1px solid ${jssVariables.colors.border}`,
    color: jssVariables.colors.text.dark,
    width: "100%",
    paddingLeft: jssVariables.normal_gap,
    ..._.get(props, "customStyles.paxBtn", {}),
  }),
});
export const RailPaxPicker = ({ adults, seniors, youths, onClick, showLabel, customStyles }) => {
  const classes = railPaxPickerStyles({ customStyles, showLabel });
  const { t } = useContext(TranslationContext) as { t: (key: string) => string };

  return (
    <div className={classes.RailPaxPicker}>
      <span className={classes.label}>{showLabel && t("guests")}</span>
      <button
        className={classes.paxBtn}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onClick();
        }}
      >
        {t("adults")}: {adults} - {t("seniors")}: {seniors} - {t("youths")}: {youths}
      </button>
    </div>
  );
};
RailPaxPicker.defaultProps = {
  adults: 1,
  seniors: 0,
  youths: 0,
  showLabel: true,
  customStyles: {},
};
RailPaxPicker.propTypes = {
  adults: PropTypes.number.isRequired,
  seniors: PropTypes.number.isRequired,
  youths: PropTypes.number.isRequired,
  customStyles: PropTypes.object.isRequired,
  showLabel: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

const searchFormNavBareStyles = createUseStyles({
  SearchFormNavBarContainer: (props) => ({
    display: "grid",
    gridTemplateColumns: "max-content 1fr max-content",
    alignItems: "center",
    gridGap: jssVariables.half_gap,
    paddingLeft: props.hasSingleProduct ? jssVariables.normal_gap : "unset",
    ..._.get(props, "customStyles.SearchFormNavBarContainer", {}),
  }),
  SearchFormNavBar: (props) => ({
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "max-content",
    gridGap: jssVariables.double_gap,
    padding: `calc(${jssVariables.normal_gap} * 1.5) 0`,
    overflowX: "auto",
    ..._.get(props, "customStyles.SearchFormNavBar", {}),
  }),
  angle: (props) => ({
    fontSize: "large",
    height: "2rem",
    width: "2rem",
    display: "grid",
    placeItems: "center",
    ..._.get(props, "customStyles.angle", {}),
  }),
  angleIcon: (props) => ({
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.angleIcon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    SearchFormNavBar: (props) => ({
      padding: 0,
      paddingBottom: jssVariables.normal_gap,
      ..._.get(props, "customStyles.SearchFormNavBar.smallscreen", {}),
    }),
    SearchFormNavBarContainer: (props) => ({
      paddingLeft: props.hasSingleProduct ? 0 : "unset",
      ..._.get(props, "customStyles.SearchFormNavBarContainer.smallscreen", {}),
    }),
    angle: {
      display: "none!important",
    },
    angleIcon: {
      display: "none",
    },
  },
});
export function SearchFormNavBar({
  customStyles,
  product_type,
  form_types,
  custom_form_types,
  setProductType,
  hasSingleProduct,
}) {
  const [formTypesLcl, setFormTypesLcl] = useState(form_types);

  const langTag = usePropertyLangTag();

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };
  const { type, brand } = useContext(StyleTypeContext);
  const { setShowNewQuotationRequestModal } = useContext(GlobalModalsContext) as {
    setShowNewQuotationRequestModal: (show: boolean) => void;
  };

  const classes = searchFormNavBareStyles({ customStyles, hasSingleProduct });

  const scrollRef = useRef(null);

  const webMetadata = useSelector(
    (state: { WebsiteMetadata: { metadata: Record<string, string> } }) =>
      state.WebsiteMetadata?.metadata ?? {},
  );

  useEffect(() => {
    setFormTypesLcl(form_types);
  }, [form_types]);

  const navBtnCStyles = cStyler({
    type,
    brand,
    objName: "mainSFormNavBtnStyles",
  });

  return formTypesLcl.length > 1 ? (
    <div className={classes.SearchFormNavBarContainer}>
      {!hasSingleProduct && (
        <div
          className={classes.angle}
          onClick={() => {
            const curr = scrollRef.current as HTMLDivElement | null;
            if (!curr) return;
            curr.scrollTo(curr.scrollLeft - 100, 0);
          }}
        >
          <InlineIcon icon="fa6-solid:angle-left" className={classes.angleIcon} />
        </div>
      )}
      <div className={classes.SearchFormNavBar} ref={scrollRef}>
        {hasSingleProduct ? (
          <MSFNavButton text={t(getProductName(product_type))} disabled={true} onClick={() => {}} />
        ) : (
          <Fragment>
            {formTypesLcl.includes("ACC") && (
              <JssProvider classNamePrefix="ACC--">
                <MSFNavButton
                  id="acc_button"
                  text={webMetadata?.[`acc_label${langTag}`] ?? ""}
                  disabled={product_type === "ACC"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("ACC")}
                />
              </JssProvider>
            )}
            {formTypesLcl.includes("FL") && (
              <JssProvider classNamePrefix="FL--">
                <MSFNavButton
                  id="fl_button"
                  text={webMetadata?.[`fl_label${langTag}`] ?? ""}
                  disabled={product_type === "FL"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("FL")}
                />
              </JssProvider>
            )}
            {formTypesLcl.includes("TRF") && (
              <JssProvider classNamePrefix="TRF--">
                <MSFNavButton
                  id="trf_button"
                  text={webMetadata?.[`trf_label${langTag}`] ?? ""}
                  disabled={product_type === "TRF"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("TRF")}
                />
              </JssProvider>
            )}
            {brand === "tmdh" && (
              <JssProvider classNamePrefix="tmdh-dream_packages--">
                <MSFNavButton
                  text={t("dream_packages")}
                  disabled={product_type === "dream_packages"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("dream_packages")}
                />
              </JssProvider>
            )}
            {formTypesLcl.includes("REST") && (
              <JssProvider classNamePrefix="REST--">
                <MSFNavButton
                  id="rest_button"
                  text={t("dining")}
                  disabled={product_type === "REST"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("REST")}
                />
              </JssProvider>
            )}
            {brand === "tmdh" && (
              <JssProvider classNamePrefix="tmdh-partying--">
                <MSFNavButton
                  text={t("partying")}
                  disabled={product_type === "partying"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("partying")}
                />
              </JssProvider>
            )}
            {formTypesLcl.includes("ACT") && (
              <JssProvider classNamePrefix="ACT--">
                <MSFNavButton
                  id="act_button"
                  text={webMetadata?.[`act_label${langTag}`] ?? ""}
                  disabled={product_type === "ACT"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("ACT")}
                />
              </JssProvider>
            )}
            {formTypesLcl.includes("FER") && (
              <JssProvider classNamePrefix="FER--">
                <MSFNavButton
                  id="fer_button"
                  text={webMetadata?.[`fer_label${langTag}`] ?? ""}
                  disabled={product_type === "FER"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("FER")}
                />
              </JssProvider>
            )}
            {formTypesLcl.includes("TRA") && (
              <JssProvider classNamePrefix="TRA--">
                <MSFNavButton
                  id="tra_button"
                  text={webMetadata?.[`tra_label${langTag}`] ?? ""}
                  disabled={product_type === "TRA"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("TRA")}
                />
              </JssProvider>
            )}
            {formTypesLcl.includes("CAR") && (
              <JssProvider classNamePrefix="CAR--">
                <MSFNavButton
                  id="car_button"
                  text={webMetadata?.[`car_label${langTag}`] ?? ""}
                  disabled={product_type === "CAR"}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setProductType("CAR")}
                />
              </JssProvider>
            )}
            {formTypesLcl.includes("QUO") && (
              <JssProvider classNamePrefix="QUO--">
                <MSFNavButton
                  id="quotation_button"
                  text="Quotation Request"
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => setShowNewQuotationRequestModal(true)}
                />
              </JssProvider>
            )}
            {custom_form_types.map((custom_form_type: string) => {
              const [label, url] = custom_form_type.split("___");
              return (
                <MSFNavButton
                  key={custom_form_type}
                  text={label}
                  disabled={false}
                  customStyles={{ ...navBtnCStyles }}
                  onClick={() => {
                    const a = document.createElement("a");
                    a.innerText = label;
                    a.href = url;
                    a.target = "_blank";
                    a.click();
                  }}
                />
              );
            })}
          </Fragment>
        )}
      </div>
      {!hasSingleProduct && (
        <div
          className={classes.angle}
          onClick={() => {
            const curr = scrollRef.current as HTMLDivElement | null;
            if (!curr) return;
            curr.scrollTo(curr.scrollLeft + 100, 0);
          }}
        >
          <InlineIcon icon="fa6-solid:angle-right" className={classes.angleIcon} />
        </div>
      )}
    </div>
  ) : null;
}
SearchFormNavBar.defaultProps = {
  customStyles: {},
  product_type: "",
  form_types: [],
  custom_form_types: [],
  hasSingleProduct: false,
};
SearchFormNavBar.propTypes = {
  customStyles: PropTypes.object.isRequired,
  product_type: PropTypes.string.isRequired,
  form_types: PropTypes.array.isRequired,
  custom_form_types: PropTypes.array.isRequired,
  setProductType: PropTypes.func.isRequired,
  hasSingleProduct: PropTypes.bool,
};

export * from "./location";
